import React, { useState, useEffect, useRef } from 'react'
import {
  Card,
  Grid,
  CardContent,
  TextField,
  FormHelperText,
  Button,
  Box
} from '@mui/material'
import { GenXDropdown } from '../../../../components/GenXDropdown'
import LanguageStrings from '../../../../i18n/locales'
import InfoVector from '../../../../assets/InfoVector.png'
import { isValidGatewayData, isValidGatewayPort, isValidIp, hasSpecialCharAtStartOrEnd } from '../../../../utils/validator'
import { styled } from '@mui/system'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Group540 from '../../../../assets/SelectDeviceType/Group540.png'
import errorCrossIcon from '../../../../assets/errorCrossIcon.svg'
import CustomToast from '../ToastComponent'
import AddGatewayDummy from '../../../../assets/AddDevice/image.png'
import './AddGateway.css'

const MAX_LENGTH = 100
const MAX_GATEWAY_DESCRIPTION_LENGTH = 500

const GatewayInformationStrings = LanguageStrings().GatewayManagementCollectionStrings.GatewayInformationComponent

const cardStyles = {
  borderRadius: '10px',
  border: '1px solid #101010',
  backgroundColor: '#272727',
  boxShadow: '1px 1px 1px 0px rgba(0, 0, 0, 0.15)',
  overflow: 'visible'
}
const infoCardStyles = {
  borderRadius: '10px',
  border: '1px solid #101010',
  backgroundColor: '#272727',
  boxShadow: '1px 1px 1px 0px rgba(0, 0, 0, 0.15)',
  marginTop: '24px'
}
const errorCardStyles = {
  borderRadius: '10px',
  border: '1px solid #101010',
  backgroundColor: '#272727',
  boxShadow: '1px 1px 1px 0px rgba(0, 0, 0, 0.15)',
  marginTop: '24px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '30px 0px 30px 0px'
}
const textStyle = {
  root: {
    backgroundColor: '#272727',
    '& .MuiInputBase-input': {
      color: '#F0F0F0',
      backgroundColor: '#404040'
    },
    '& .MuiInputLabel-root': {
      color: '#F0F0F0',
      '&.Mui-focused': {
        color: '#F0F0F0'
      },
      // Conditional color based on disabled state
      '&.Mui-disabled': {
        color: '#B0B0B0'
      }
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: '#00000000'
      },
      '&:hover fieldset': {
        borderColor: '#00000000'
      },
      '&.Mui-focused fieldset': {
        borderColor: '#00000000'
      }
    },
    '& .Mui-disabled': {
      color: '#B0B0B0 !important'
    }
  },
  width: 'min(410px, 100%)'
}
const CustomCardContent = styled(CardContent)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  width: '309px',
  height: '240px',
  borderRadius: '10px',
  border: '1px solid var(--General-Secondary, #303030)',
  background: 'var(--General-Secondary, #303030)',
  position: 'relative'
})

const FileInput = styled('input')({
  display: 'none'
})

const FileButton = styled(Button)({
  width: '309px',
  padding: '6px 24px',
  marginTop: '5px',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '10px',
  borderRadius: '4px',
  border:
    '1.5px solid var(--component-light-buttons-regular-primary-outline-blue-a-20071-b-3, #64C3FF)',
  background: '#202020',
  boxShadow: '1px 1px 1px 0px rgba(0, 0, 0, 0.30)'
})

interface GatewayType {
  asset_type_id: string
  asset_type_name: string
  category_type_id: string
  category_type_name: string
}

interface ParentProps {
  gatewayType: GatewayType[] | null
  gatewayName: string
  setGatewayName: React.Dispatch<React.SetStateAction<string>>
  gatewayDescription: string
  setGatewayDescription: React.Dispatch<React.SetStateAction<string>>
  gatewayNameErrorInit: boolean
  setGatewayNameErrorInit: React.Dispatch<React.SetStateAction<boolean>>
  gatewayDescriptionErrorInit: boolean
  setGatewayDescriptionErrorInit: React.Dispatch<React.SetStateAction<boolean>>
  portNumber: string
  setPortNumber: React.Dispatch<React.SetStateAction<string>>
  portNumberErrorInit: boolean
  setPortNumberErrorInit: React.Dispatch<React.SetStateAction<boolean>>
  applicationType: string
  setApplicationType: React.Dispatch<React.SetStateAction<string>>
  applicationTypeErrorInit: boolean
  setApplicationTypeErrorInit: React.Dispatch<React.SetStateAction<boolean>>
  location: string
  setLocation: React.Dispatch<React.SetStateAction<string>>
  locationErrorInit: boolean
  setLocationErrorInit: React.Dispatch<React.SetStateAction<boolean>>
  ipAddress: string
  setIpAddress: React.Dispatch<React.SetStateAction<string>>
  ipAddressErrorInit: boolean
  setIpAddressErrorInit: React.Dispatch<React.SetStateAction<boolean>>
  previewImage: string | null
  setPreviewImage: React.Dispatch<React.SetStateAction<string | null>>
  assetType: LabelValue | null
  setAssetType: React.Dispatch<React.SetStateAction<LabelValue | null>>
  assetModel: LabelValue | null
  setAssetModel: React.Dispatch<React.SetStateAction<LabelValue | null>>
  gatewayImage: any
  setGatewayImage: React.Dispatch<React.SetStateAction<any>>
  gatewayId: string
  protocolOptions: LabelValue[] | []
  protocol: LabelValue | null
  setProtocol: React.Dispatch<React.SetStateAction<LabelValue | null>>
  timezoneOptions: LabelValue[] | []
  timezone: LabelValue | null
  setTimezone: React.Dispatch<React.SetStateAction<LabelValue | null>>
}

interface LabelValue {
  label: string
  value: string
}

const GatewayInformation = (props: ParentProps): JSX.Element => {
  const [gatewayTypeOptions, setGatewayTypeOptions] = useState<LabelValue[]>([])
  const [imageDimensions, setImageDimensions] = useState({ width: '136px', height: '136px' })
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    // istanbul ignore next
    if (props.gatewayImage !== undefined && props.gatewayImage !== null) setImageDimensions({ width: '260px', height: '215px' })
  }, [])

  useEffect(() => {
    // istanbul ignore else
    if (props.gatewayType !== null) {
      setGatewayTypeOptions(convertToGatewayTypeOptions(props.gatewayType))
    }
  }, [props.gatewayType])

  /* Currently filtering Transparent Modem, this check will be removed when other gateways are supported */
  const convertToGatewayTypeOptions = (gatewayTypes: GatewayType[]): LabelValue[] =>
    gatewayTypes
      .filter((gatewayType) => gatewayType.asset_type_name === 'TransparentModem')
      .map((gatewayType) => ({
        label: 'Transparent Gateway',
        value: gatewayType.asset_type_id
      }))

  const handleGatewayTypeSelect = (selectedGatewayType: LabelValue): void => {
    props.setAssetType(selectedGatewayType)
  }
  const handleProtocolTypeSelect = (selectedProtocol: LabelValue): void => {
    props.setProtocol(selectedProtocol)
  }
  const handleTimezoneTypeSelect = (selectedTimezone: LabelValue): void => {
    props.setTimezone(selectedTimezone)
  }

  const gatewayNameErrorMessage = (): string | null => {
    if (props?.gatewayName?.length > MAX_LENGTH) {
      return GatewayInformationStrings.GatewayNameLengthError
    } else if (hasSpecialCharAtStartOrEnd(props?.gatewayName)) {
      return GatewayInformationStrings.NotAllowedError
    } else if (!isValidGatewayData(props?.gatewayName)) {
      return GatewayInformationStrings.GatewayNameErrorMsg
    }
    return null
  }
  const gatewayDescErrorMessage = (): string | null => {
    if (props?.gatewayDescription?.length > MAX_GATEWAY_DESCRIPTION_LENGTH) {
      return GatewayInformationStrings.GatewayDescLengthError
    } else if (hasSpecialCharAtStartOrEnd(props?.gatewayDescription)) {
      return GatewayInformationStrings.NotAllowedError
    } else if (!isValidGatewayData(props?.gatewayDescription)) {
      return GatewayInformationStrings.GatewayDescErrorMsg
    }
    return null
  }

  const isGatewayNameNotValid = (): boolean => {
    return (
      !isValidGatewayData(props.gatewayName) || props.gatewayName.length > MAX_LENGTH
    )
  }
  const isGatewayDescriptionNotValid = (): boolean => {
    return (
      !isValidGatewayData(props.gatewayDescription) || props.gatewayDescription.length > MAX_GATEWAY_DESCRIPTION_LENGTH
    )
  }
  const isPortNumberNotValid = (): boolean => {
    const port = parseInt(props.portNumber, 10)
    return !(port > 0 && port <= 65535 && isValidGatewayPort(props.portNumber) && props.portNumber.length <= 5)
  }
  const isIpAddressNotValid = (): boolean => {
    return (
      !isValidIp(props.ipAddress) ||
      props.ipAddress.length === 0
    )
  }
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const files = event.target.files
    const maxSize = 100 * 1024
    const allowedTypes = ['image/jpeg', 'image/png']
    const alphanumericRegex = /^[a-zA-Z0-9]+$/
    // istanbul ignore else
    if (files !== null && files.length > 0) {
      // Validate file size
      if (files[0].size > maxSize) {
        CustomToast({
          type: 'error',
          heading: GatewayInformationStrings.ImageSizeError,
          message: GatewayInformationStrings.ImageSizeErrorMsg
        })
        props.setPreviewImage(AddGatewayDummy)
        event.target.value = '' // Clearing the file input value
        return
      }
      // Validate file type
      if (!allowedTypes.includes(files[0].type)) {
        CustomToast({
          type: 'error',
          heading: GatewayInformationStrings.ImageTypesError,
          message: GatewayInformationStrings.ImageTypesErrorMsg
        })
        props.setPreviewImage(AddGatewayDummy)
        event.target.value = '' // Clearing the file input value
        return
      }
      // Validate file name (without extension)
      const fileNameWithoutExtension = files[0].name.split('.')[0]
      /* istanbul ignore next */
      if (!alphanumericRegex.test(fileNameWithoutExtension)) {
        CustomToast({
          type: 'error',
          heading: 'Invalid File Name',
          message: 'File name should contain only alphanumeric characters.'
        })
        props.setPreviewImage(AddGatewayDummy)
        event.target.value = '' // Clearing the file input value
        return
      }
      /* istanbul ignore next */
      if (fileNameWithoutExtension.length > 20) {
        CustomToast({
          type: 'error',
          heading: 'Invalid File Name',
          message: 'File Name is greater than 20 alphanumeric characters.'
        })
        props.setPreviewImage(AddGatewayDummy)
        event.target.value = '' // Clearing the file input value
        return
      }
      const reader = new FileReader()
      // istanbul ignore next
      reader.onloadend = () => {
        const image = new Image()
        image.src = reader.result as string
        image.onload = () => {
          setImageDimensions({ width: '260px', height: '215px' }) // width: 309, height: 240
        }
        props.setPreviewImage(reader.result as string)
        props.setGatewayImage(files[0])
      }
      reader.readAsDataURL(files[0])
    }
  }
  const handleBrowseClick = (): void => {
    fileInputRef.current?.click()
  }

  return (
    <>
    <div className="gateway-info card-grid">
      <>
        <Card sx={cardStyles}>
          <CardContent>
            <div className="gateway-info-content">
              <p className="gateway-info-heading">
                {GatewayInformationStrings.GatewayInformation}
              </p>
            </div>
            <Grid className='responsive-grid' container spacing={2} columns={{ md: 6, sm: 6, xs: 2, xl: 8 }}>
              <Grid item xs={2} sm={3} md={2}>
                <span style={{ fontSize: '15px', padding: '10px 3px', display: 'block', color: '#F0F0F0' }}>
                  {GatewayInformationStrings.GatewayType}
                </span>
                <GenXDropdown
                  width={'100%'}
                  placeholder='Select a gateway type'
                  value={props.assetType}
                  options={gatewayTypeOptions}
                  onSelect={handleGatewayTypeSelect}
                  data-testId="gateway-type-dropdown"
                />
              </Grid>
              <Grid item xs={2} sm={3} md={2}>
                <span style={{ fontSize: '15px', padding: '8px 3px', display: 'block', color: '#F0F0F0' }}>
                  {GatewayInformationStrings.GatewayName}
                  <sup style={{ color: '#B0B0B0', fontSize: '16px', fontStyle: 'normal', fontWeight: '500', lineHeight: '24px' }}>*</sup>
                  <Tooltip title="Enter Gateway Name with maximum 100 alphanumeric characters" placement="right" arrow>
                    <IconButton size="small">
                      <img style={{ width: '12px', height: '12px', marginLeft: '5px' }} src={InfoVector} />
                    </IconButton>
                  </Tooltip>
                </span>
                <TextField
                  id="custom-textfield"
                  variant="outlined"
                  data-testId="gateway-name"
                  placeholder="Enter Gateway Name"
                  InputLabelProps={{
                    disabled: true
                  }}
                  value={props.gatewayName}
                  onChange={(e) => {
                    props.setGatewayName(e.target.value)
                    props.setGatewayNameErrorInit(true)
                  }}
                  disabled={props.gatewayId !== ''}
                  size="small"
                  autoComplete="off"
                  className={`custom-textfield ${isGatewayNameNotValid() && props.gatewayNameErrorInit ? 'error' : ''}`}
                  sx={{
                    ...textStyle.root,
                    width: '100%'
                  }}
                />
                {isGatewayNameNotValid() && props.gatewayNameErrorInit && (
                    // istanbul ignore next
                    <div style={{ display: 'flex' }}>
                      <div style={{ marginTop: '3px' }}>
                        <img src={errorCrossIcon} alt="Error" />
                      </div>
                      <FormHelperText sx={{ color: '#EE3426', paddingLeft: '5px', width: '92%' }} className="error-msg">
                        {gatewayNameErrorMessage()}
                      </FormHelperText>
                    </div>
                )}
              </Grid>
              <Grid item xs={2} sm={3} md={2}>
                <span style={{ fontSize: '15px', padding: '10px 3px', display: 'block', color: '#F0F0F0' }}>
                  {GatewayInformationStrings.Protocol}
                  <sup style={{ color: '#B0B0B0', fontSize: '16px', fontStyle: 'normal', fontWeight: '500' }}>*</sup>
                </span>
                <GenXDropdown
                  width={'100%'}
                  placeholder='Select Protocol'
                  value={props.protocol}
                  options={props.protocolOptions}
                  onSelect={handleProtocolTypeSelect}
                  data-testId="protocol-dropdown"
                />
              </Grid>
              <Grid item xs={2} sm={3} md={2} >
                <span style={{ fontSize: '15px', padding: '10px 3px', display: 'block', color: '#F0F0F0' }}>
                  {GatewayInformationStrings.TimeZone}
                  <sup style={{ color: '#B0B0B0', fontSize: '16px', fontStyle: 'normal', fontWeight: '500' }}>*</sup>
                </span>
                <GenXDropdown
                  width={'100%'}
                  placeholder='Select Time Zone'
                  value={props.timezone}
                  options={props.timezoneOptions}
                  onSelect={handleTimezoneTypeSelect}
                  data-testId="timezone-dropdown"
                />
              </Grid>
              <Grid item xs={2} sm={3} md={2} >
                <span style={{ fontSize: '15px', padding: '8px 3px', display: 'block', color: '#F0F0F0' }}>
                  {GatewayInformationStrings.GatewayDescription}
                  <sup style={{ color: '#B0B0B0', fontSize: '16px', fontStyle: 'normal', fontWeight: '500', lineHeight: '24px' }}>*</sup>
                  <Tooltip title="Enter Gateway description with maximum 500 alphanumeric characters" placement="right" arrow>
                    <IconButton size="small">
                      <img style={{ width: '12px', height: '12px', marginLeft: '5px' }} src={InfoVector} />
                    </IconButton>
                  </Tooltip>
                </span>
                <TextField
                  id="custom-textfield-description"
                  variant="outlined"
                  data-testId="gateway-description"
                  placeholder="Enter Gateway Description"
                  InputLabelProps={{
                    disabled: true
                  }}
                  value={props.gatewayDescription}
                  onChange={(e) => {
                    props.setGatewayDescription(e.target.value)
                    props.setGatewayDescriptionErrorInit(true)
                  }}
                  disabled={props.gatewayId !== ''}
                  size="small"
                  autoComplete="off"
                  className={`custom-textfield-description ${isGatewayDescriptionNotValid() && props.gatewayDescriptionErrorInit ? 'error' : ''}`}
                  sx={{
                    ...textStyle.root,
                    width: '100%'
                  }}
                />
                {isGatewayDescriptionNotValid() && props.gatewayDescriptionErrorInit && (
                    // istanbul ignore next
                    <div style={{ display: 'flex' }}>
                      <div style={{ marginTop: '3px' }}>
                        <img src={errorCrossIcon} alt="Error" />
                      </div>
                      <FormHelperText sx={{ color: '#EE3426', paddingLeft: '5px' }} className="error-msg">
                        {gatewayDescErrorMessage()}
                      </FormHelperText>
                    </div>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </>

      {props.assetType === null
        ? (
          <Card sx={errorCardStyles}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-around',
                width: '679px',
                height: '261px',
                flexDirection: 'row',
                alignItems: 'center',
                flexShrink: 0,
                borderRadius: '10px',
                border: '1px solid #404040',
                background: '#404040'
              }}
            >
              <div>
                <img
                  style={{
                    width: '161.997px',
                    height: '193.975px',
                    flexShrink: 0
                  }}
                  src={Group540}
                />
              </div>
              <div>
                <div className="gateway-info-content">
                  <p className="gateway-info-heading">
                    {GatewayInformationStrings.SelectGatewayType}
                  </p>
                  <p
                    style={{
                      color:
                        'var(--typography-dark-base-default-f-0-f-0-f-0, #F0F0F0)',
                      fontFeatureSettings: "'clig' off, 'liga' off",
                      fontFamily: '"Honeywell Sans Web"',
                      fontSize: '16px',
                      fontStyle: 'normal',
                      fontWeight: 500,
                      lineHeight: '24px'
                    }}
                  >
                    This could be because:
                  </p>
                  <p
                    style={{
                      color:
                        'var(--typography-dark-base-default-f-0-f-0-f-0, #F0F0F0)',
                      fontFeatureSettings: "'clig' off, 'liga' off",
                      fontFamily: '"Honeywell Sans Web"',
                      fontSize: '16px',
                      fontStyle: 'normal',
                      fontWeight: 500,
                      lineHeight: '24px'
                    }}
                  >
                    . You have not selected any gateway type.
                  </p>
                </div>
              </div>
            </Box>
          </Card>
          )
        : (
          <Card sx={infoCardStyles}>
            <CardContent>
              <div className="gateway-info-content">
                <p className="gateway-info-heading">
                  {GatewayInformationStrings.GatewayDetails}
                </p>
              </div>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, marginTop: '10px' }}>
                <div className="image-section" style={{ backgroundColor: '#202020', marginRight: '20px', padding: '20px', width: 'max-content' }}>
                  <CustomCardContent>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}>
                      {props.previewImage !== null && (
                        <img
                          src={props.previewImage}
                          alt="Preview"
                          style={{ width: imageDimensions.width, height: imageDimensions.height }}
                        />
                      )}
                    </div>
                    <FileInput
                      ref={fileInputRef}
                      type="file"
                      id="fileInput"
                      data-testid="browse-image-file"
                      accept="image/jpeg, image/png"
                      onChange={handleFileChange}
                    />
                  </CustomCardContent>
                  <FileButton
                    data-testid="browse-image-button"
                    onClick={handleBrowseClick}
                    style={{
                      fontFamily: 'Honeywell Sans Web',
                      textTransform: 'unset'
                    }}
                  >
                    Add Photo
                  </FileButton>
                </div>
                <Box className="info-section" sx={{ flexGrow: 1 }}>
                  <Grid container spacing={2} style={{ height: '135px' }}>
                    <Grid item xs={12}>
                      <span style={{ fontSize: '15px', padding: '10px 3px', display: 'block', color: '#F0F0F0' }}>
                        {GatewayInformationStrings.IpAddress}
                        <sup style={{ color: '#B0B0B0', fontSize: '16px', fontStyle: 'normal', fontWeight: '500', lineHeight: '24px' }}>*</sup>
                        <Tooltip title={GatewayInformationStrings.IpAddressTooltip} placement="right" arrow>
                          <IconButton size="small">
                            <img style={{ width: '12px', height: '12px', marginLeft: '5px' }} src={InfoVector} />
                          </IconButton>
                        </Tooltip>
                      </span>
                      <TextField
                        id="custom-textfield-ip-address"
                        variant="outlined"
                        data-testId="ip-address"
                        placeholder="Enter IP Address"
                        InputLabelProps={{
                          disabled: true
                        }}
                        disabled={props.gatewayId !== ''}
                        value={props.ipAddress}
                        onChange={(e) => {
                          props.setIpAddress(e.target.value)
                          props.setIpAddressErrorInit(true)
                        }}
                        size="small"
                        autoComplete="off"
                        className={`custom-textfield ${isIpAddressNotValid() && props.ipAddressErrorInit ? 'error' : ''}`}
                        sx={{
                          ...textStyle.root,
                          width: textStyle.width
                        }}
                      />
                      {isIpAddressNotValid() && props.ipAddressErrorInit && (
                          // istanbul ignore next
                          <Box sx={{ display: 'flex', width: textStyle.width }}>
                            <div style={{ marginTop: '3px' }}>
                              <img src={errorCrossIcon} alt="Error" />
                            </div>
                            <FormHelperText
                                sx={{
                                  color: '#EE3426',
                                  paddingLeft: '5px'
                                }}
                                className="error-msg"
                            >
                              {GatewayInformationStrings.IPAddressError}
                            </FormHelperText>
                          </Box>
                      )}
                    </Grid>
                  </Grid>
                  {props.protocol?.label !== 'MODBUS-TCP/IP' && (
                    <Grid container spacing={2} style={{ height: '135px' }}>
                      <Grid item xs={12}>
                        <span style={{ fontSize: '15px', padding: '10px 3px', display: 'block', color: '#F0F0F0' }}>
                          {GatewayInformationStrings.PortNumber}
                          <sup style={{ color: '#B0B0B0', fontSize: '16px', fontStyle: 'normal', fontWeight: '500', lineHeight: '24px' }}>*</sup>
                        </span>
                        <TextField
                          id="custom-textfield-port-number"
                          variant="outlined"
                          data-testId="port-number"
                          placeholder="Enter Port Number"
                          InputLabelProps={{
                            disabled: true
                          }}
                          disabled={props.gatewayId !== ''}
                          value={props.portNumber}
                          onChange={(e) => {
                            props.setPortNumber(e.target.value)
                            props.setPortNumberErrorInit(true)
                          }}
                          size="small"
                          autoComplete="off"
                          className={`custom-textfield ${isPortNumberNotValid() && props.portNumberErrorInit ? 'error' : ''}`}
                          sx={{
                            ...textStyle.root,
                            width: textStyle.width
                          }}
                        />
                        {isPortNumberNotValid() && props.portNumberErrorInit && (
                          // istanbul ignore next
                          <Box sx={{ display: 'flex', width: textStyle.width }}>
                            <div style={{ marginTop: '3px' }}>
                              <img src={errorCrossIcon} alt="Error" />
                            </div>
                            <FormHelperText sx={{ color: '#EE3426', paddingLeft: '5px' }} className="error-msg">
                              {GatewayInformationStrings.PortNumberError}
                            </FormHelperText>
                          </Box>
                        )}
                      </Grid>
                    </Grid>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
          )}
    </div>
    </>
  )
}
export default GatewayInformation
